import type {
  InventoryMapObject,
  InventoryMapObjectData,
  InventoryMapObjectType,
  InventoryMapObjectTypes,
} from '@/widgets'
import { InventoryLineTypeData } from '@/entities/Inventory/Main'

const ZOOM_CLUSTER_GEOMETRY_TYPES = new Set(['Polygon', 'LineString', 'Circle'])

export abstract class InventObject {
  protected properties: any
  protected feature!: InventoryMapObject

  public coordinates!: number[] | number[][] | number[][][]
  public geometry: any = {}

  protected constructor(objectData: InventoryMapObjectData, objectType: InventoryMapObjectTypes) {
    this.properties = {
      ...objectType,
      styles: {
        fill: objectType.styles.fill,
        stroke: {
          ...objectType.styles.stroke,
          dash: InventoryLineTypeData[objectType.styles.stroke.line.code],
        },
      },
      object_type: objectData.object_type,
      object_invent_type: {
        ...objectData.object_invent_type,
        id: objectData.invent_objects_type_id,
      },
      object_id: objectData.id,
      object_name: objectData.name,
      isProjecting: objectData.is_projecting,
      objectFields: objectData.fields || [],
      parent_id: objectData.parent_object_id,
    }

    this.coordinates = objectData.coordinates
  }

  public getId(): number {
    return this.properties.object_id
  }

  public getFeature(): InventoryMapObject {
    return this.feature
  }

  public getType(): InventoryMapObjectType {
    return this.feature.properties.object_type
  }

  public getSubType() {
    return {
      ...this.properties.object_invent_type,
      parent: this.getType(),
    }
  }

  public getFigureSubType(): string {
    return this.feature.properties.figure.geom_subtype
  }

  public isPoint(): boolean {
    return this.feature.geometry.type === 'Point'
  }

  public isLine(): boolean {
    return this.feature.geometry.type === 'LineString'
  }

  public isPolygon(): boolean {
    return this.feature.geometry.type === 'Polygon'
  }

  public getZoom(): number {
    return this.feature.properties.properties.zoom
  }

  /**
   * Возвращает тип геометрии из настроек инвентаризационного типа.
   * Для окружности фактическая GeoJSON-геометрия является точкой, поэтому
   * проверять только `feature.geometry.type` недостаточно.
   */
  public getFigureGeomType(): string {
    return this.properties.object_invent_type?.figure?.geom_type ?? ''
  }

  /**
   * Проверяет, нужно ли представить геометрию точкой в общем кластере
   * на текущем масштабе карты.
   */
  public shouldClusterByZoom(currentZoom: number): boolean {
    const zoomCluster = Number(
      this.properties.object_invent_type?.properties?.zoom_cluster ??
        this.feature.properties.properties?.zoom_cluster,
    )

    return (
      ZOOM_CLUSTER_GEOMETRY_TYPES.has(this.getFigureGeomType()) &&
      Number.isFinite(zoomCluster) &&
      zoomCluster > 0 &&
      currentZoom <= zoomCluster
    )
  }

  public getObjectTypeId(): number {
    return this.feature.properties.object_type.id
  }

  public getObjectSubTypeId(): number {
    return this.feature.properties.id
  }

  public getGeometry() {
    return this.feature.geometry
  }

  /**
   * Получения координат для отображения на карте
   */
  public getMapCoords(): number[] | number[][] | number[][][] {
    return this.feature.geometry.coordinates
  }

  public getMapFlat() {
    return this.coordinates
  }

  public getFields() {
    return this.properties.objectFields
  }

  /**
   * Функция получения координат для метки данных
   */
  public getLabelCoords(): number[] {
    // TODO реализация должна переопределяться в потомках
    return []
  }

  /**
   * Функция трансформации точек для бд
   * @param coords
   */
  public transformCoords(coords: any): number[][] {
    // TODO реализация должна переопределяться в потомках
    return coords
  }

  /**
   * Функция получения координат для отрисовки точек трансформации
   */
  public getTransformCoords(): number[][] {
    // TODO реализация должна переопределяться в потомках
    return [[]]
  }

  /**
   * Функция получения координат самого объекта
   */
  public getFeatureCoords(): number[] {
    // TODO реализация должна переопределяться в потомках
    return []
  }

  /**
   * Функция установки координат одной точки
   * @param index
   * @param coords
   */
  public setTransformPointCoords(index: number, coords: any[]) {
    // TODO реализация должна переопределяться в потомках
  }

  /**
   * Функция установки координат всех точек
   * @param coords
   */
  public setTransformPointsCoords(coords: any[]) {
    // TODO реализация должна переопределяться в потомках
  }

  /**
   * Функция удаления точки
   * @param index
   */
  public deletePoint(index: number) {
    // TODO реализация должна переопределяться в потомках
  }

  public getStyles() {
    return this.feature.properties.styles
  }

  public getParentId() {
    return this.properties.parent_id
  }

  public getIsBind() {
    return this.feature.properties.properties.is_bind
  }

  public getName() {
    return this.properties.object_name
  }

  public getIsMapUngroup() {
    return this.properties.object_type.is_map_ungroup
  }
}
